import { useState } from "react";
import {
  Chip,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";
import { getUserDetail } from "../../../../../apis";

/**
 * UserTooltipChip Component
 * Displays a chip that shows user details in a tooltip on hover
 */
export const UserTooltipChip = ({ userId, label, t }) => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchUserData = async () => {
    if (hasFetched || isLoading) return;

    setIsLoading(true);
    setHasError(false);

    try {
      const response = await getUserDetail(userId);
      if (response?.user) {
        setUserData(response.user);
      } else {
        setHasError(true);
      }
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  };

  const handleMouseEnter = () => {
    if (!hasFetched) {
      fetchUserData();
    }
  };

  const renderTooltipContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} color="inherit" />
          <Typography variant="body2">{t("Bitacora.loading_user")}</Typography>
        </Box>
      );
    }

    if (hasError || !userData) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" color="error">
            {t("Bitacora.user_not_found")}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 1, minWidth: 200 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {t("Bitacora.user_details")}
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {userData.first_name && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("Bitacora.first_name")}:
              </Typography>
              <Typography variant="body2">{userData.first_name}</Typography>
            </Box>
          )}
          {userData.last_name && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("Bitacora.last_name")}:
              </Typography>
              <Typography variant="body2">{userData.last_name}</Typography>
            </Box>
          )}
          {userData.email && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("Bitacora.email")}:
              </Typography>
              <Typography variant="body2">{userData.email}</Typography>
            </Box>
          )}
          {userData.department && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("Bitacora.department")}:
              </Typography>
              <Typography variant="body2">{userData.department}</Typography>
            </Box>
          )}
          {userData.position && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("Bitacora.position")}:
              </Typography>
              <Typography variant="body2">{userData.position}</Typography>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Tooltip
      title={renderTooltipContent()}
      arrow
      placement="top"
      onMouseEnter={handleMouseEnter}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 2,
            "& .MuiTooltip-arrow": {
              color: "background.paper",
            },
          },
        },
      }}
    >
      <Chip
        label={label}
        size="small"
        color="secondary"
        sx={{
          fontSize: 11,
          height: 22,
          mb: 0.5,
          cursor: "pointer",
        }}
      />
    </Tooltip>
  );
};
